import {defineStore} from "pinia";
import {computed, ref} from "vue";
import {useFirebaseAuth } from "vuefire";
import {API_GATEWAY_URL} from "@/constants";
import {
  AllAttemptsFailedError,
  ContentApiJsonError,
  RedirectRulesNotFoundError, UnauthorisedError,
  UnknownError,
} from "@/errors";
import {
  ContentApiResponse,
} from "@/types";
import {
  fetchFirstSuccess,
} from "@/lib/fetcher";
import {MissingClaim} from "@/errors.ts";
import {buildUrlWithParams, getNlaUsers} from "./utils";
import {logger, LogCode} from '@/logger';

type fetchAccessResponse = {
  url: string;
};

const isRedirectRulesNotFoundError = (
  error: unknown
): error is RedirectRulesNotFoundError =>
  error instanceof ContentApiJsonError &&
  error.code === "RedirectRulesNotFound";

export const useAccessStore = defineStore("access", () => {
  const _articleIds = ref<string>("");
  const _mmoId = ref<string>("");
  const _redirectUrl = ref<string>("");
  const _user = useFirebaseAuth()!.currentUser;
  const _fetchAccessUrl = computed(() => {
    const params = {
      articleIds: _articleIds.value,
      mmoId: _mmoId.value,
      redirectUrl: _redirectUrl.value,
      apiVersion: "v1",
    }
    const url = buildUrlWithParams(`access-check`, API_GATEWAY_URL, params);
    return url.toString();
  });
  const isFinished = ref<boolean>(false);
  const error = ref<string>();
  const _fetchAccess = async (correlationId: string): Promise<
    ContentApiResponse<fetchAccessResponse>
  > => {
    if (!_user) throw new UnauthorisedError("User not found");
    const tokenString = await _user.getIdToken();
    const nlaUsers = await getNlaUsers(_user);
    if (nlaUsers === undefined){
      throw new MissingClaim("nlaUsers");
    }
    try {
      const resp = await fetchFirstSuccess(nlaUsers, _fetchAccessUrl.value, tokenString, correlationId);
      return await resp.json();
    } catch (e) {
      let error = e;
      // Extract error if only one request was made
      if(e instanceof AllAttemptsFailedError){
        error = e.getSingularError() ?? e;
      }

      if(isRedirectRulesNotFoundError(error)){
        throw new RedirectRulesNotFoundError();
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new UnknownError();
    }
  };

  const fetchAccess = async (
    articleIds: string,
    mmoId: string,
    redirectUrl: string,
    correlationId: string
  ) => {
    isFinished.value = false;
    _articleIds.value = articleIds;
    _mmoId.value = mmoId;
    _redirectUrl.value = redirectUrl;
    try {
      const json = await _fetchAccess(correlationId);
      window.open(json.data.url, "_self");
      return Promise.resolve();
    } catch (e) {
      if (e instanceof Error) {
        error.value = `${e.message}\n\nReference ID: ${correlationId}`;
      }
      let nlaUserIds = undefined;
      if (_user) {
        const nlaUsers = await getNlaUsers(_user);
        nlaUserIds = nlaUsers?.map(user => user.id) ?? []
      }
      logger.error(
        `Failed to fetch access`,
        {
          correlationId,
          dataFields: {
            userIds: nlaUserIds,
            articleIds,
            mmoId,
          },
          redirectUrl,
          url: _fetchAccessUrl.value,
          component: 'stores/access.ts',
          pathname: window.location.pathname,
          route: window.location.pathname + window.location.search,
          logCode: LogCode.FAILED_TO_FETCH_ACCESS
        },
        e
      );
    } finally {
      isFinished.value = true;
    }
  };
  return {
    isFinished,
    error,
    fetchAccess,
  };
});
